import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  Paper,
  Slider,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';

import {
  useGameCamerasQuery,
  useUpdateGameCameraMutation,
  useCreateGameCamerasMutation,
  type GameCamera,
  type UpdateGameCameraData,
} from '@/features/cameras/hooks';
import { getGameCamerasQueryKey } from '@/features/cameras/hooks/useGameCamerasQuery';
import useCurrentGameQuery from '@/features/games/hooks/useCurrentGameQuery';

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Tab Panel component
function TabPanel(props: { children?: React.ReactNode; value: number; index: number }) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`camera-tabpanel-${index}`}
      aria-labelledby={`camera-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface CameraConfigFormV2Props {
  gameId: string;
  onCamerasChange?: () => void;
  readOnly?: boolean;
}

// Validated Number TextField Component
interface ValidatedNumberFieldProps {
  label: string;
  value: number | null | undefined;
  onChange: (value: number) => void;
  disabled?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  sx?: any;
}

const ValidatedNumberField: React.FC<ValidatedNumberFieldProps> = ({
  label,
  value,
  onChange,
  disabled,
  placeholder,
  min,
  max,
  step = 0.01,
  sx,
}) => {
  const [localValue, setLocalValue] = useState<string>(value?.toString() || '');
  const [error, setError] = useState<string>('');
  const debouncedValue = useDebounce(localValue, 800);
  const isFirstRender = useRef(true);
  const prevValueRef = useRef<number | null | undefined>(value);

  // Update local value when prop value changes externally (not from user input)
  useEffect(() => {
    if (value !== prevValueRef.current) {
      setLocalValue(value?.toString() || '');
      prevValueRef.current = value;
    }
  }, [value]);

  // Validate and call onChange when debounced value changes
  useEffect(() => {
    // Skip validation on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (debouncedValue === '' || debouncedValue.trim() === '') {
      setError('Value is required');
      return;
    }

    const numValue = parseFloat(debouncedValue);

    if (isNaN(numValue)) {
      setError('Please enter a valid number');
      return;
    }

    if (min !== undefined && numValue < min) {
      setError(`Value must be at least ${min}`);
      return;
    }

    if (max !== undefined && numValue > max) {
      setError(`Value must be at most ${max}`);
      return;
    }

    setError('');

    // Only call onChange if the value actually changed
    if (numValue !== prevValueRef.current) {
      prevValueRef.current = numValue;
      onChange(numValue);
    }
  }, [debouncedValue, min, max]); // Removed onChange from dependencies

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  };

  return (
    <TextField
      sx={sx}
      label={label}
      value={localValue}
      onChange={handleChange}
      disabled={disabled}
      placeholder={placeholder}
      error={!!error}
      helperText={error}
      slotProps={{
        htmlInput: {
          step: step.toString(),
        },
      }}
    />
  );
};

const CameraConfigFormV2: React.FC<CameraConfigFormV2Props> = ({
  gameId,
  onCamerasChange,
  readOnly = false,
}) => {
  // State for active tab
  const [activeTab, setActiveTab] = useState(0);

  // State for tilt values (one per camera) with debouncing
  const [localTiltValues, setLocalTiltValues] = useState<Record<number, number>>({});
  // State for roll values (one per camera) with debouncing
  const [localRollValues, setLocalRollValues] = useState<Record<number, number>>({});

  const [cameraVersions, setCameraVersions] = useState<Record<number, number>>({});
  const [degradedPanelOpen, setDegradedPanelOpen] = useState<Record<number, boolean>>({});
  const [statusMessageDraft, setStatusMessageDraft] = useState<Record<number, string>>({});
  const [savingStatusId, setSavingStatusId] = useState<number | null>(null);

  const queryClient = useQueryClient();
  const cameraUpdateChainRef = useRef<Record<number, Promise<unknown>>>({});
  const lastStatusSigRef = useRef<Record<number, string>>({});
  const handleCameraChangeRef = useRef<
    (gameCameraId: number, field: keyof UpdateGameCameraData, value: any) => Promise<void>
  >(async () => {});

  // Queries
  const {
    data: gameCameras = [],
    isLoading: camerasLoading,
    error: camerasError,
    refetch: refetchCameras,
  } = useGameCamerasQuery(gameId);


  const { data: currentGame } = useCurrentGameQuery();

  // Mutations
  const updateCameraMutation = useUpdateGameCameraMutation();
  const createCamerasMutation = useCreateGameCamerasMutation();

  // Derived state - use venue's cameraSetup
  const cameraSetup = useMemo(() => {
    return currentGame?.venue?.cameraSetup || null;
  }, [currentGame]);

  // Get tracking config from the first camera (they all share the same tracking config from venue)
  const trackingConfig = gameCameras.length > 0 ? gameCameras[0]?.trackingConfig : null;

  useEffect(() => {
    const newTiltValues: Record<number, number> = {};
    const newRollValues: Record<number, number> = {};
    const newVersions: Record<number, number> = {};

    gameCameras.forEach((camera) => {
      if (localTiltValues[camera.id] === undefined) {
        newTiltValues[camera.id] = camera.tilt || 0;
      }
      if (localRollValues[camera.id] === undefined) {
        newRollValues[camera.id] = camera.roll || 0;
      }
      if (cameraVersions[camera.id] === undefined) {
        newVersions[camera.id] = camera.version || 1;
      }
    });

    if (Object.keys(newTiltValues).length > 0) {
      setLocalTiltValues((prev) => ({ ...prev, ...newTiltValues }));
    }
    if (Object.keys(newRollValues).length > 0) {
      setLocalRollValues((prev) => ({ ...prev, ...newRollValues }));
    }
    if (Object.keys(newVersions).length > 0) {
      setCameraVersions((prev) => ({ ...prev, ...newVersions }));
    }
  }, [gameCameras]);

  useEffect(() => {
    gameCameras.forEach((gc) => {
      const sig = `${gc.version ?? 0}|${gc.statusMessage ?? ''}`;
      if (lastStatusSigRef.current[gc.id] === sig) return;
      lastStatusSigRef.current[gc.id] = sig;
      setStatusMessageDraft((d) => ({ ...d, [gc.id]: (gc.statusMessage ?? '') as string }));
      setDegradedPanelOpen((p) => ({
        ...p,
        [gc.id]: Boolean((gc.statusMessage ?? '').trim()),
      }));
    });
  }, [gameCameras]);

  // Debounce tilt updates - use debounce hook for each camera's tilt value
  useEffect(() => {
    const timeouts: Record<number, NodeJS.Timeout> = {};

    Object.entries(localTiltValues).forEach(([cameraIdStr, tiltValue]) => {
      const cameraId = parseInt(cameraIdStr, 10);
      const camera = gameCameras.find((gc) => gc.id === cameraId);

      // Only update if the value has actually changed from what's in the server
      // Treat null/undefined as 0 for comparison purposes
      const serverValue = camera?.tilt ?? 0;
      if (camera && serverValue !== tiltValue) {
        timeouts[cameraId] = setTimeout(() => {
          void handleCameraChangeRef.current(cameraId, 'tilt', tiltValue);
        }, 500); // 500ms debounce delay
      }
    });

    // Cleanup timeouts
    return () => {
      Object.values(timeouts).forEach((timeout) => clearTimeout(timeout));
    };
  }, [localTiltValues, gameCameras]);

  // Debounce roll updates - use debounce hook for each camera's roll value
  useEffect(() => {
    const timeouts: Record<number, NodeJS.Timeout> = {};

    Object.entries(localRollValues).forEach(([cameraIdStr, rollValue]) => {
      const cameraId = parseInt(cameraIdStr, 10);
      const camera = gameCameras.find((gc) => gc.id === cameraId);

      // Only update if the value has actually changed from what's in the server
      // Treat null/undefined as 0 for comparison purposes
      const serverValue = camera?.roll ?? 0;
      if (camera && serverValue !== rollValue) {
        timeouts[cameraId] = setTimeout(() => {
          void handleCameraChangeRef.current(cameraId, 'roll', rollValue);
        }, 500); // 500ms debounce delay
      }
    });

    // Cleanup timeouts
    return () => {
      Object.values(timeouts).forEach((timeout) => clearTimeout(timeout));
    };
  }, [localRollValues, gameCameras]);

  // Effects
  useEffect(() => {
    if (onCamerasChange) {
      onCamerasChange();
    }
  }, [gameCameras, onCamerasChange]);

  // Reset activeTab if it's out of bounds
  useEffect(() => {
    if (activeTab >= gameCameras.length) {
      setActiveTab(0);
    }
  }, [gameCameras.length, activeTab]);

  // Handlers
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleCreateCamerasFromSetup = async () => {
    if (!cameraSetup?.id) return;

    try {
      await createCamerasMutation.mutateAsync({
        gameId,
        data: { cameraSetupId: cameraSetup.id },
      });
      refetchCameras();
    } catch (error) {
      console.error('Failed to create cameras from setup:', error);
    }
  };

  const handleCameraChange = useCallback(
    async (gameCameraId: number, field: keyof UpdateGameCameraData, value: any) => {
      if (readOnly) return;

      const queryKey = getGameCamerasQueryKey(gameId);

      const runUpdate = async () => {
        const list = queryClient.getQueryData<GameCamera[]>(queryKey) ?? gameCameras;
        const gameCamera = list.find((gc) => gc.id === gameCameraId);
        if (!gameCamera) {
          console.error('Game camera not found:', gameCameraId);
          return;
        }

        const currentVersion = gameCamera.version ?? 1;

        const result = await updateCameraMutation.mutateAsync({
          cameraId: gameCamera.id,
          data: { [field]: value, version: currentVersion },
          gameId,
        });

        const newVersion = result?.data?.version;
        if (newVersion != null) {
          setCameraVersions((prev) => ({
            ...prev,
            [gameCameraId]: newVersion,
          }));
        }
      };

      const prev = cameraUpdateChainRef.current[gameCameraId] ?? Promise.resolve();
      const next = prev.catch(() => {}).then(runUpdate);
      cameraUpdateChainRef.current[gameCameraId] = next;
      await next;
    },
    [readOnly, gameId, gameCameras, queryClient, updateCameraMutation],
  );

  handleCameraChangeRef.current = handleCameraChange;

  const handleCameraNestedChange = async (
    gameCameraId: number,
    parent: 'position' | 'forward',
    field: 'x' | 'y' | 'z',
    value: number,
  ) => {
    if (readOnly) return;

    const updateField = `${parent}${field.toUpperCase()}` as keyof UpdateGameCameraData;
    await handleCameraChange(gameCameraId, updateField, value);
  };

  const isStatusMessagingDirty = (gc: GameCamera) => {
    const draft = (statusMessageDraft[gc.id] ?? (gc.statusMessage ?? '')).trim();
    const server = (gc.statusMessage ?? '').trim();
    if (gc.isLive === false) {
      return draft !== server;
    }
    const degradedUi = degradedPanelOpen[gc.id] ?? server.length > 0;
    if (!degradedUi && server.length > 0) return true;
    if (degradedUi && draft !== server) return true;
    return false;
  };

  const saveStatusForCamera = async (gc: GameCamera) => {
    if (readOnly) return;
    setSavingStatusId(gc.id);
    try {
      const draft = (statusMessageDraft[gc.id] ?? (gc.statusMessage ?? '')).trim();
      const server = (gc.statusMessage ?? '').trim();
      const degradedUi = degradedPanelOpen[gc.id] ?? server.length > 0;
      let value: string | null;
      if (gc.isLive === false) {
        value = draft === '' ? null : draft;
      } else {
        value = !degradedUi ? null : draft === '' ? null : draft;
      }
      await handleCameraChange(gc.id, 'statusMessage', value);
    } finally {
      setSavingStatusId(null);
    }
  };


  // Helper functions
  const getValue = (gameCamera: GameCamera | undefined, field: string) => {
    if (!gameCamera) {
      return null;
    }
    return (gameCamera as any)[field];
  };



  if (camerasLoading) {
    return (
      <Box sx={{ minHeight: '200px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (camerasError) {
    return (
      <Alert severity="error">Failed to load camera configuration: {camerasError.message}</Alert>
    );
  }

  if (gameCameras.length === 0) {
    return (
      <Box component="form" noValidate sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Card>
          <CardHeader
            title="Camera System Configuration"
            subheader="No cameras configured for this game"
          />
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              This game doesn't have any cameras configured. You can create cameras from the venue's
              camera setup template.
            </Alert>

            {cameraSetup ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Venue Camera Setup:</strong> {cameraSetup.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {cameraSetup.cameras?.length || 0} cameras available • Units: {cameraSetup.units}
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleCreateCamerasFromSetup}
                  disabled={readOnly || createCamerasMutation.isPending}
                  sx={{ mt: 1 }}
                >
                  {createCamerasMutation.isPending ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Creating Cameras...
                    </>
                  ) : (
                    'Create Cameras from Venue Setup'
                  )}
                </Button>
              </Box>
            ) : (
              <Alert severity="warning">
                No camera setup found for this venue. Please contact an administrator.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box component="form" noValidate sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Card>
        <CardHeader
          title={
            <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
              <SettingsIcon />
              <Box>
                <Typography variant="h6">Camera System Configuration</Typography>
                {cameraSetup && (
                  <Typography variant="caption" color="text.secondary">
                    Setup: {cameraSetup.name} • Units: {cameraSetup.units}
                  </Typography>
                )}
              </Box>
            </Box>
          }
          subheader={`${gameCameras.length} camera${gameCameras.length !== 1 ? 's' : ''} configured`}
        />
        <Divider />

        <CardContent>
          {/* Camera Tabs */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Cameras
            </Typography>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {gameCameras.map((gameCamera) => (
                <Tab
                  key={`tab-${gameCamera.cameraId}-${gameCamera.camIndex}`}
                  label={
                    <Box sx={{ gap: 1, display: 'flex', alignItems: 'center' }}>
                      {getValue(gameCamera, 'displayName') || `Camera ${gameCamera.camIndex + 1}`}
                    </Box>
                  }
                />
              ))}
            </Tabs>

            {gameCameras.map((gameCamera, index) => (
              <TabPanel
                key={`camera-${gameCamera.cameraId}-${gameCamera.camIndex}`}
                value={activeTab}
                index={index}
              >
                <Paper elevation={3} sx={{ p: 3 }}>
                  {/* Basic Camera Settings */}
                  <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>

                    <TextField
                      sx={{ flex: '1 1 300px', minWidth: '250px' }}
                      label="Display Name"
                      value={getValue(gameCamera, 'displayName') || ''}
                      onChange={(e) =>
                        handleCameraChange(gameCamera.id, 'displayName', e.target.value)
                      }
                      disabled={readOnly}
                      placeholder={`Camera ${gameCamera.camIndex + 1}`}
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={getValue(gameCamera, 'isLive') ?? true}
                          onChange={(e) =>
                            handleCameraChange(gameCamera.id, 'isLive', e.target.checked)
                          }
                          disabled={readOnly}
                        />
                      }
                      label="Enabled"
                    />
                  </Box>

                  {getValue(gameCamera, 'isLive') !== false && (
                    <Box sx={{ mb: 3 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              degradedPanelOpen[gameCamera.id] ??
                              Boolean(String(getValue(gameCamera, 'statusMessage') ?? '').trim())
                            }
                            onChange={(e) => {
                              const on = e.target.checked;
                              setDegradedPanelOpen((prev) => ({ ...prev, [gameCamera.id]: on }));
                              if (!on) {
                                setStatusMessageDraft((prev) => ({ ...prev, [gameCamera.id]: '' }));
                                void handleCameraChange(gameCamera.id, 'statusMessage', null);
                              }
                            }}
                            disabled={readOnly}
                          />
                        }
                        label="Degraded"
                      />
                      {(degradedPanelOpen[gameCamera.id] ??
                        Boolean(String(getValue(gameCamera, 'statusMessage') ?? '').trim())) && (
                        <Box sx={{ mt: 2 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={4}
                            label="Status message"
                            helperText="Shown to viewers on hover while the camera stays enabled. Save when done."
                            slotProps={{ htmlInput: { maxLength: 280 } }}
                            value={statusMessageDraft[gameCamera.id] ?? (getValue(gameCamera, 'statusMessage') as string) ?? ''}
                            onChange={(e) =>
                              setStatusMessageDraft((d) => ({
                                ...d,
                                [gameCamera.id]: e.target.value,
                              }))
                            }
                            disabled={readOnly}
                          />
                          <Button
                            variant="contained"
                            sx={{ mt: 2 }}
                            disabled={
                              readOnly ||
                              savingStatusId === gameCamera.id ||
                              !isStatusMessagingDirty(gameCamera)
                            }
                            onClick={() => void saveStatusForCamera(gameCamera)}
                          >
                            {savingStatusId === gameCamera.id ? (
                              <>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                Saving…
                              </>
                            ) : (
                              'Save status'
                            )}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}

                  {getValue(gameCamera, 'isLive') === false && (
                    <Box sx={{ mb: 3 }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        label="Status message"
                        helperText="Shown to viewers when this camera is disabled. Save when done."
                        slotProps={{ htmlInput: { maxLength: 280 } }}
                        value={statusMessageDraft[gameCamera.id] ?? (getValue(gameCamera, 'statusMessage') as string) ?? ''}
                        onChange={(e) =>
                          setStatusMessageDraft((d) => ({
                            ...d,
                            [gameCamera.id]: e.target.value,
                          }))
                        }
                        disabled={readOnly}
                      />
                      <Button
                        variant="contained"
                        sx={{ mt: 2 }}
                        disabled={
                          readOnly ||
                          savingStatusId === gameCamera.id ||
                          !isStatusMessagingDirty(gameCamera)
                        }
                        onClick={() => void saveStatusForCamera(gameCamera)}
                      >
                        {savingStatusId === gameCamera.id ? (
                          <>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Saving…
                          </>
                        ) : (
                          'Save status'
                        )}
                      </Button>
                    </Box>
                  )}

                  {/* Position Settings */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      Position (x, y, z)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <ValidatedNumberField
                        sx={{ flex: '1 1 50px', minWidth: '120px' }}
                        label="X"
                        step={0.01}
                        value={getValue(gameCamera, 'positionX')}
                        onChange={(value) =>
                          handleCameraNestedChange(gameCamera.id, 'position', 'x', value)
                        }
                        disabled={readOnly}
                      />

                      <ValidatedNumberField
                        sx={{ flex: '1 1 50px', minWidth: '120px' }}
                        label="Y"
                        step={0.01}
                        value={getValue(gameCamera, 'positionY')}
                        onChange={(value) =>
                          handleCameraNestedChange(gameCamera.id, 'position', 'y', value)
                        }
                        disabled={readOnly}
                      />

                      <ValidatedNumberField
                        sx={{ flex: '1 1 50px', minWidth: '120px' }}
                        label="Z"
                        step={0.01}
                        value={getValue(gameCamera, 'positionZ')}
                        onChange={(value) =>
                          handleCameraNestedChange(gameCamera.id, 'position', 'z', value)
                        }
                        disabled={readOnly}
                      />
                    </Box>
                  </Box>

                  {/* Forward Direction Settings */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      Forward Direction (x, y, z)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <ValidatedNumberField
                        sx={{ flex: '1 1 50px', minWidth: '120px' }}
                        label="X"
                        step={0.1}
                        min={-1}
                        max={1}
                        value={getValue(gameCamera, 'forwardX')}
                        onChange={(value) =>
                          handleCameraNestedChange(gameCamera.id, 'forward', 'x', value)
                        }
                        disabled={readOnly}
                      />

                      <ValidatedNumberField
                        sx={{ flex: '1 1 50px', minWidth: '120px' }}
                        label="Y"
                        step={0.1}
                        min={-1}
                        max={1}
                        value={getValue(gameCamera, 'forwardY')}
                        onChange={(value) =>
                          handleCameraNestedChange(gameCamera.id, 'forward', 'y', value)
                        }
                        disabled={readOnly}
                      />

                      <ValidatedNumberField
                        sx={{ flex: '1 1 50px', minWidth: '120px' }}
                        label="Z"
                        step={0.1}
                        min={-1}
                        max={1}
                        value={getValue(gameCamera, 'forwardZ')}
                        onChange={(value) =>
                          handleCameraNestedChange(gameCamera.id, 'forward', 'z', value)
                        }
                        disabled={readOnly}
                      />
                    </Box>
                  </Box>

                  {/* Tilt Setting */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      Tilt (degrees)
                    </Typography>
                    <Slider
                      value={localTiltValues[gameCamera.id] ?? getValue(gameCamera, 'tilt') ?? 0}
                      onChange={(_e, value) => {
                        setLocalTiltValues((prev) => ({
                          ...prev,
                          [gameCamera.id]: value as number,
                        }));
                      }}
                      valueLabelDisplay="auto"
                      min={-90}
                      max={90}
                      disabled={readOnly}
                      marks={[
                        { value: -90, label: '-90°' },
                        { value: -45, label: '-45°' },
                        { value: 0, label: '0°' },
                        { value: 45, label: '45°' },
                        { value: 90, label: '90°' },
                      ]}
                    />
                  </Box>

                  {/* Roll Setting */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      Roll (degrees)
                    </Typography>
                    <Slider
                      value={localRollValues[gameCamera.id] ?? getValue(gameCamera, 'roll') ?? 0}
                      onChange={(_e, value) => {
                        setLocalRollValues((prev) => ({
                          ...prev,
                          [gameCamera.id]: value as number,
                        }));
                      }}
                      valueLabelDisplay="auto"
                      min={-180}
                      max={180}
                      disabled={readOnly}
                      marks={[
                        { value: -180, label: '-180°' },
                        { value: -90, label: '-90°' },
                        { value: 0, label: '0°' },
                        { value: 90, label: '90°' },
                        { value: 180, label: '180°' },
                      ]}
                    />
                  </Box>

                  {/* FOV Settings */}
                  <Box sx={{ mb: 3, gap: 3 }}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      Zoom Range Levels
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <ValidatedNumberField
                        sx={{ flex: '1 1 200px', maxWidth: '300px' }}
                        label="Minimal Zoom (FOV Min)"
                        step={1}
                        min={1}
                        max={(getValue(gameCamera, 'fovMax') as number) - 1 || 178}
                        value={getValue(gameCamera, 'fovMin')}
                        onChange={(value) => handleCameraChange(gameCamera.id, 'fovMin', value)}
                        disabled={readOnly}
                        placeholder="35"
                      />
                      <ValidatedNumberField
                        sx={{ flex: '1 1 200px', maxWidth: '300px' }}
                        label="Maximum Zoom (FOV Max)"
                        step={1}
                        min={(getValue(gameCamera, 'fovMin') as number) + 1 || 2}
                        max={179}
                        value={getValue(gameCamera, 'fovMax')}
                        onChange={(value) => handleCameraChange(gameCamera.id, 'fovMax', value)}
                        disabled={readOnly}
                        placeholder="45"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Note: Minimal Zoom (FOV Min) must be less than Maximum Zoom (FOV Max)
                    </Typography>
                  </Box>

                  {/* Offset */}
                  <Box sx={{ mb: 3, display: 'flex', gap: 3 }}>
                    <ValidatedNumberField
                      sx={{ flex: '1 1 300px', maxWidth: '400px' }}
                      label="Offset"
                      step={0.1}
                      value={getValue(gameCamera, 'offset')}
                      onChange={(value) => handleCameraChange(gameCamera.id, 'offset', value)}
                      disabled={readOnly}
                    />
                  </Box>

                  {/* Lens Information (Read-only) */}
                  {gameCamera.lens && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Lens Information (From Template)
                      </Typography>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Lens settings are inherited from the camera setup template and cannot be
                        modified per game.
                      </Alert>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Lens:</strong> {gameCamera.lens.name}
                        <br />
                        <strong>Curve Points:</strong> {gameCamera.lens.curveAngle.length} points
                        <br />
                        <strong>Angles:</strong> {gameCamera.lens.curveAngle.join(', ')}
                        <br />
                        <strong>Radius:</strong> {gameCamera.lens.curveRadius.join(', ')}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </TabPanel>
            ))}
          </Box>

          {/* Tracking Specifications (Read-only) */}
          {trackingConfig && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Tracking Specifications (From Template)
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Tracking settings are inherited from the camera setup template.
              </Alert>
              <Paper elevation={3} sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: 3,
                  }}
                >
                  <TextField
                    fullWidth
                    label="SLERP Rate"
                    type="number"
                    value={trackingConfig.slerpRate}
                    slotProps={{ input: { readOnly: true } }}
                  />

                  <TextField
                    fullWidth
                    label="LERP Rate"
                    type="number"
                    value={trackingConfig.lerpRate}
                    slotProps={{ input: { readOnly: true } }}
                  />

                  <TextField
                    fullWidth
                    label="vDamp"
                    type="number"
                    value={trackingConfig.vDamp}
                    slotProps={{ input: { readOnly: true } }}
                  />
                </Box>
              </Paper>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CameraConfigFormV2;
